import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { FaGraduationCap, FaBriefcase, FaTrophy, FaFileAlt, FaDownload, FaFilePdf, FaExternalLinkAlt } from 'react-icons/fa'
import profilePhoto from '/photo/cv_photo.png'
import './About.css'

const CV_DRIVE_ID = '1u8vxtGp75aIlV2fW3vxWkTHrEY5ixL9z'
const CV_PREVIEW_URL = `https://drive.google.com/file/d/${CV_DRIVE_ID}/preview`
const CV_DOWNLOAD_URL = `https://drive.google.com/uc?export=download&id=${CV_DRIVE_ID}`
const CV_VIEW_URL = `https://drive.google.com/file/d/${CV_DRIVE_ID}/view`

const About = () => {
  const [showCvPreview, setShowCvPreview] = useState(false)

  const education = [
    {
      degree: "MS, Computer Engineering — Artificial Intelligence",
      university: "Gebze Technical University",
      location: "Gebze, Turkey",
      period: "02/2026 - In Progress",
      gpa: null
    },
    {
      degree: "BS, Electronics Engineering (Ranked 4th in Department)",
      university: "Gebze Technical University",
      location: "Gebze, Turkey",
      period: "09/2021 - 06/2025",
      gpa: "3.67/4.0"
    }
  ]

  const experience = [
    {
      title: "Freelance: Embedded Software Engineer",
      company: "KAUST (King Abdullah University of Science and Technology)",
      location: "Saudi Arabia",
      period: "12/2025 - 03/2026",
      responsibilities: [
        "Developed a native macOS application connected via BLE to send control commands to the embedded device, trigger audio recording to an SD card for data collection in real showering environments, receive AI model outputs (including used and wasted water metrics) over BLE, store the collected data, enable audio playback, and support manual labeling of voice data to evaluate model accuracy in real-world scenarios.",
        "Developed embedded software for smart shower applications to detect water wastage by analyzing microphone sensor data using AI, enabling autonomous decision-making.",
        "Developed FreeRTOS-based embedded software and a BLE-connected mobile application to provide users with detailed information about shower water usage and wastage, synchronize data, and allow selection of various comfort modes based on user preferences.",
        "Designed and developed a simple PCB shield for the nRF52840 with an SD card interface to collect audio data for AI model training."
      ]
    },
    {
      title: "Visiting Student Research Program: Embedded Software Engineer",
      company: "KAUST (King Abdullah University of Science and Technology)",
      location: "Saudi Arabia",
      period: "09/2025 - 11/2025",
      responsibilities: [
        "Developed Edge AI models for smart shower applications to detect water wastage using microphone sensor data, leveraging Edge Impulse for model training and deployment on Nordic nRF52840.",
        "Implemented embedded firmware for a wake-up radio system designed to activate ultra–low-power IoT nodes in remote pipeline monitoring environments, integrating the AS3933 wake-up receiver with ESP32 and the SX1278 transceiver on an STM32-based transmitter."
      ]
    },
    {
      title: "BMS Embedded Software Engineer",
      company: "AFPM Technology",
      location: "Gebze, Turkey",
      period: "06/2022 - 09/2025",
      responsibilities: [
        "Developed embedded software for electric scooters' Battery Management System (BMS) using BQ76940, BQ34z100 ICs, and STM32 microcontroller. Implemented 2-level battery protection and CAN bus communication with vehicle ECU.",
        "Developed modular BMS software for industrial vehicles (golf carts, boats) with LTC6813/LTC6811 ICs and STM32. Enabled CAN bus communication with motor controllers (e.g., Zapi) and implemented LiFePO4 battery modeling (Simulink) and SoC estimation via Extended Kalman Filter.",
        "Developed a CAN bus bootloader for firmware updates, comprising embedded software (STM32 microcontroller in C) and a PC-side application (Python) for seamless deployment."
      ]
    },
    {
      title: "Electronics Engineering Intern",
      company: "MAKERSAN",
      location: "Gebze, Turkey",
      period: "07/2024 - 08/2024",
      responsibilities: [
        "Development of embedded software for a joystick testing device using STM32G4."
      ]
    },
    {
      title: "Electronics Engineering Intern",
      company: "TechSafe Solutions",
      location: "Istanbul, Turkey",
      period: "06/2024 - 07/2024",
      responsibilities: [
        "Work with Zephyr RTOS and implement MCUBootloader encrypted firmware update for stg-800 PLC."
      ]
    }
  ]

  const projects = [
    {
      title: "Senior Project: Electrical Car Tracking System",
      role: "Electronics Engineering Student",
      period: "06/2024 - 06/2025",
      description: [
        "Developed an IoT-based vehicle tracking system powered by the STM32U575 microcontroller and ThreadX RTOS, integrated with Azure IoT Central. The system collects data from electric vehicles via CAN bus and publishes it securely to the cloud, enabling real-time monitoring through a custom-developed dashboard.",
        "Implemented mobile connectivity using the Quectel EG915 4G/LTE module and real-time GPS tracking with the Quectel L26ADR module. The system is enhanced with sensor fusion by integrating ST IMU sensors LIS3MDL (magnetometer) and LSM6DSOX (accelerometer and gyroscope) for improved accuracy.",
        "Designed and implemented a CAN bus bootloader and Firmware Update Over-the-Air (FOTA) mechanism, allowing remote firmware updates for the vehicles."
      ]
    },
    {
      title: "GTU ROVER",
      role: "Electronics Team Member and Drone Team Leader",
      period: "06/2022 - 06/2024",
      description: [
        "A science platform has been developed, powered by an ESP32 microcontroller, capable of measuring CO, CO2, O2, humidity, temperature, and altitude. The data is transmitted wirelessly to another ESP32 via ESPNow, and control for stepper motors, valves, and a pump is integrated through a web interface.",
        "An autonomous drone software system has been developed, utilizing ArduPilot as the foundation, with ROS serving as the operating system, and MAVROS for bridging between MAVLink and ROS. The system also integrates OpenCV for ArUco code recognition and utilizes a PID algorithm for precision landing.",
        "Attending to the Anatolian Rover Challenge and joining the University Rover Challenge."
      ]
    },
    {
      title: "TÜBİTAK 2209-B University-Industry Collaboration project",
      role: "Electronics Engineering Student",
      period: "03/2023 - 03/2024",
      description: [
        "Developed Battery Management System and State of Charge Estimation for Micro Electric Vehicles in collaboration with AFPM Technology."
      ]
    },
    {
      title: "STM32G0 Tutorials Project",
      role: "Register Level Projects for STM32",
      period: "11/2023 - 02/2024",
      description: [
        "The project includes ARM Assembly and C code for STM32G0 and is designed as a tutorial for beginners and published on my GitHub account. The project includes various types of examples, such as GPIO control, timer and external interrupts, SysTick and watchdog Timer, PWM control, UART and I2C communication, seven-segment display driver, knock detection using MPU6050, and a sound sensor."
      ]
    },
    {
      title: "Code Master by OBSS",
      role: "Electronics Engineering Student",
      period: "06/2023",
      description: [
        "Achieved 2nd place in the Code Master competition at Gebze Technical University."
      ]
    }
  ]

  const publications = [
    {
      title: "Node Based Anomaly Detection with Autoencoders",
      authors: "Fouad Aladhami, Erhan Gök, Ahmet Güneş",
      venue: "33rd IEEE Conference on Signal Processing and Communications Applications",
      year: "2025",
      status: "Published",
      doi: "https://ieeexplore.ieee.org/document/11112083",
      doiText: "10.1109/SIU66497.2025.11112083",
      abstract: "In many systems, individuals are identified and monitored after behavioral issues arise. In this study, anomaly detection is used to identify suspects based on their movement patterns before incidents occur. Unlike other studies, this approach relies on predefined node points and travel times between them instead of visual data. Autoencoder and variational autoencoder models were used to analyze individuals' travel routes, and anomalies were detected based on reconstruction error. The results indicate that the variational autoencoder model produces more precise and accurate results compared to the autoencoder. It demonstrates superior performance in detecting complex anomaly patterns in behavioral movement data."
    }
  ]

  const skills = {
    main: [
      "Embedded Software Development",
      "Battery Management System",
      "STM32 Software Development",
      "Embedded C",
      "CAN Bus",
      "I2C",
      "SPI",
      "UART",
      "Python",
      "MATLAB",
      "Simulink",
      "C++",
      "Machine Learning",
      "Battery Modeling",
      "Digital Signal Processing",
      "Version Control Using Git",
      "ROS",
      "Edge AI"
    ],
    interests: [
      "Robotics",
      "Embodied AI",
      "Analog and Digital Communication Systems",
      "Natural Language Processing"
    ]
  }

  return (
    <div className="about">
      {/* Hero Section */}
      <section className="about-hero section-padding">
        <div className="container">
          <motion.div 
            className="about-hero-content"
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="about-text">
              <h1>About Me</h1>
              <div className="about-summary">
                <p>
                  Embedded Software Engineer focused on Battery Management Systems (BMS) and electric vehicle applications. 
                  Proficient in firmware development for STM32 microcontrollers, CAN bus communication, and BMS. 
                  Experience includes battery modeling (Simulink), State of Charge (SoC) estimation using Extended Kalman Filter, 
                  and bootloader development for firmware updates.
                </p>
                <p>
                  Collaborated on multidisciplinary projects involving IoT systems (Azure Cloud) and RTOS environments (Zephyr, ThreadX). 
                  Seeking to advance expertise in embedded systems, signal processing, robotics, and AI.
                </p>
              </div>
            </div>
            <div className="about-image">
              <img 
                src={profilePhoto} 
                alt="Fouad Aladhami" 
                className="about-photo"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Education Section */}
      <section className="education section-padding bg-secondary">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2><FaGraduationCap /> Education</h2>
          </motion.div>

          <div className="education-grid">
            {education.map((edu, index) => (
              <motion.div 
                key={index}
                className="education-card card"
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <div className="education-content">
                  <h3>{edu.degree}</h3>
                  <h4>{edu.university}</h4>
                  <p className="location">{edu.location}</p>
                  <p className="period">{edu.period}</p>
                  {edu.gpa && <p className="gpa">GPA: {edu.gpa}</p>}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section className="experience section-padding">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2><FaBriefcase /> Professional Experience</h2>
          </motion.div>

          <div className="timeline">
            {experience.map((job, index) => (
              <motion.div 
                key={index}
                className="timeline-item"
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <div className="timeline-content card">
                  <h3>{job.title}</h3>
                  <h4>{job.company}</h4>
                  <p className="location">{job.location}</p>
                  <p className="period">{job.period}</p>
                  <ul className="responsibilities">
                    {job.responsibilities.map((resp, idx) => (
                      <li key={idx}>{resp}</li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section className="skills section-padding bg-secondary">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2>Technical Skills</h2>
          </motion.div>

          <div className="skills-content">
            <motion.div 
              className="skills-category"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3>Main Skills</h3>
              <div className="skills-list">
                {skills.main.map((skill, index) => (
                  <span key={index} className="skill-tag">{skill}</span>
                ))}
              </div>
            </motion.div>

            <motion.div 
              className="skills-category"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h3>Interests</h3>
              <div className="skills-list">
                {skills.interests.map((skill, index) => (
                  <span key={index} className="skill-tag interest">{skill}</span>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section className="projects section-padding">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2><FaTrophy /> Projects & Achievements</h2>
          </motion.div>

          <div className="projects-grid">
            {projects.map((project, index) => (
              <motion.div 
                key={index}
                className="project-card card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                viewport={{ once: true }}
              >
                <h3>{project.title}</h3>
                <h4>{project.role}</h4>
                <p className="period">{project.period}</p>
                <ul>
                  {project.description.map((desc, idx) => (
                    <li key={idx}>{desc}</li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Publications Section */}
      <section className="publications section-padding bg-secondary">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2><FaFileAlt /> Publications</h2>
          </motion.div>

          {publications.map((pub, index) => (
            <motion.div 
              key={index}
              className="publication-card card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h3>{pub.title}</h3>
              <p className="authors">{pub.authors}</p>
              <p className="venue">{pub.venue} ({pub.year})</p>
              <span className="status">{pub.status}</span>
              <p className="abstract"><strong>Abstract:</strong> {pub.abstract}</p>
              {pub.doi && (
                <p className="doi">
                  <strong>DOI:</strong>{' '}
                  <a href={pub.doi} target="_blank" rel="noopener noreferrer">
                    {pub.doiText} <FaExternalLinkAlt size={10} />
                  </a>
                </p>
              )}
            </motion.div>
          ))}
        </div>
      </section>

      {/* CV Section */}
      <section className="cv-section section-padding">
        <div className="container">
          <motion.div 
            className="section-header text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2><FaFilePdf /> Access my CV</h2>
          </motion.div>

          <motion.div
            className="cv-actions"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <a 
              href={CV_DOWNLOAD_URL} 
              className="cv-btn cv-download-btn"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaDownload /> Download CV
            </a>
            <a 
              href={CV_VIEW_URL} 
              className="cv-btn cv-view-btn"
              target="_blank"
              rel="noopener noreferrer"
            >
              <FaExternalLinkAlt /> Open in Google Drive
            </a>
            <button 
              className={`cv-btn cv-preview-btn ${showCvPreview ? 'active' : ''}`}
              onClick={() => setShowCvPreview(!showCvPreview)}
            >
              <FaFilePdf /> {showCvPreview ? 'Hide Preview' : 'Preview CV'}
            </button>
          </motion.div>

          {showCvPreview && (
            <motion.div 
              className="cv-preview-container"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.4 }}
            >
              <iframe
                src={CV_PREVIEW_URL}
                className="cv-preview-iframe"
                title="CV Preview"
                allow="autoplay"
              />
            </motion.div>
          )}
        </div>
      </section>
    </div>
  )
}

export default About
